'use client'

import { useState, useEffect, useRef } from 'react'
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useToast } from "@/components/ui/use-toast"
import { Checkbox } from "@/components/ui/checkbox"
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { getCurrentDate } from '@/lib/utils/date'
import { handleHttpError } from '@/lib/utils/toast-utils'

interface ContractYear {
    year: number
    yrOption: string | null
    salary: number
}

interface PositionOverride {
    positionCode: string
    year: number
}

interface PlayerEditFormProps {
    playerId: number
    onClose: () => void
    onSuccess: () => void
    setIsEditing: (isEditing: boolean) => void
}

const PLAYER_TYPES = [
    { value: 'hitter', label: 'Hitter' },
    { value: 'pitcher', label: 'Pitcher' },
    { value: 'both', label: 'Both' },
] as const

const THROWS_OPTIONS = [
    { value: 'L', label: 'Left' },
    { value: 'R', label: 'Right' },
] as const

const BATS_OPTIONS = [
    { value: 'L', label: 'Left' },
    { value: 'R', label: 'Right' },
    { value: 'B', label: 'Both' },
] as const

const POSITION_OPTIONS = [
    { value: 'C', label: 'C' },
    { value: '1B', label: '1B' },
    { value: '2B', label: '2B' },
    { value: '3B', label: '3B' },
    { value: 'SS', label: 'SS' },
    { value: 'OF', label: 'OF' },
    { value: 'UTIL', label: 'UTIL' },
    { value: 'SP', label: 'SP' },
    { value: 'RP', label: 'RP' },
    { value: 'P', label: 'P' },
] as const

const OPTION_TYPES = [
    { value: '__none__', label: 'None' },
    { value: 'OC', label: 'Club Option' },
    { value: 'OP', label: 'Player Option' },
    { value: 'OO', label: 'Other Option' },
] as const

const getOptionLabel = (options: readonly { value: string, label: string }[], value: string | null) => {
    return options.find(option => option.value === value)?.label || 'Select...'
}

type ModificationType = 'modification' | 'extension' | null

const INITIAL_FORM_STATE = {
    firstName: '',
    lastName: '',
    type: '',
    throws: '',
    bats: '',
    mlbPlayerId: '',
    exemptFromPlayerCount: false,
    contractYears: [] as ContractYear[],
    positionOverrides: [] as PositionOverride[],
    modificationType: null as ModificationType,
    extensionSignedDate: '',
    cotsUrl: '',
    hasArbYears: false,
}

export function PlayerEditForm({ playerId, onClose, onSuccess, setIsEditing }: PlayerEditFormProps) {
    const { toast } = useToast()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [formData, setFormData] = useState(INITIAL_FORM_STATE)
    const dataLoadedRef = useRef(false)
    const queryClient = useQueryClient()
    const formContainerRef = useRef<HTMLFormElement>(null)

    // Debug logging
    useEffect(() => {
        console.log('[PlayerEditForm] Component mounted, playerId:', playerId)
        console.log('[PlayerEditForm] formContainerRef.current:', formContainerRef.current)
        return () => {
            console.log('[PlayerEditForm] Component unmounting')
        }
    }, [playerId])

    // Fetch current player data including contracts and position overrides
    const { data: playerData, isLoading } = useQuery({
        queryKey: ['player', playerId, 'details'],
        queryFn: async () => {
            const response = await fetch(`/api/player/${playerId}`)
            if (!response.ok) {
                throw new Error('Failed to fetch player data')
            }
            return response.json()
        }
    })

    // Update form data when player data is loaded
    useEffect(() => {
        if (playerData && !dataLoadedRef.current) {
            console.log('Setting player data:', playerData)
            setFormData({
                firstName: playerData.firstName || '',
                lastName: playerData.lastName || '',
                type: playerData.type || '',
                throws: playerData.throws || '',
                bats: playerData.bats || '',
                mlbPlayerId: playerData.mlbPlayerId?.toString() || '',
                exemptFromPlayerCount: playerData.exemptFromPlayerCount || false,
                contractYears: playerData.contractYears || [],
                positionOverrides: playerData.positionOverrides || [],
                modificationType: null,
                extensionSignedDate: '',
                cotsUrl: '',
                hasArbYears: false,
            })
            dataLoadedRef.current = true
        }
    }, [playerData])

    // Reset dataLoadedRef when playerId changes
    useEffect(() => {
        dataLoadedRef.current = false
    }, [playerId])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (formData.modificationType === null) return
        setIsSubmitting(true)
        setIsEditing(true)

        try {
            const response = await fetch(`/api/player/${playerId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    type: formData.type,
                    throws: formData.throws,
                    bats: formData.bats,
                    mlbPlayerId: formData.mlbPlayerId ? parseInt(formData.mlbPlayerId) : null,
                    exemptFromPlayerCount: formData.exemptFromPlayerCount,
                    contractYears: formData.contractYears,
                    positionOverrides: formData.positionOverrides,
                    ...(formData.modificationType === 'extension' ? {
                        isExtension: true,
                        extensionSignedDate: formData.extensionSignedDate,
                        cotsUrl: formData.cotsUrl,
                        hasArbYears: formData.hasArbYears,
                    } : {}),
                }),
            })

            if (!response.ok) {
                const error = await response.json()
                throw error
            }

            const result = await response.json()

            // Invalidate all queries related to this player
            await queryClient.invalidateQueries({
                queryKey: ['player', playerId]
            })

            toast({
                title: "Success",
                description: result.message || "Player updated successfully",
            })
            onSuccess()
        } catch (error) {
            console.error('Error updating player:', error)
            handleHttpError(error)
        } finally {
            setIsSubmitting(false)
            setIsEditing(false)
        }
    }

    const handleAddContractYear = () => {
        setFormData(prev => {
            const maxYear = prev.contractYears.length > 0
                ? Math.max(...prev.contractYears.map(c => c.year))
                : new Date().getFullYear()
            return {
                ...prev,
                contractYears: [...prev.contractYears, { year: maxYear + 1, yrOption: null, salary: 0 }]
            }
        })
    }

    const handleRemoveContractYear = (index: number) => {
        setFormData(prev => ({
            ...prev,
            contractYears: prev.contractYears.filter((_, i) => i !== index)
        }))
    }

    const handleAddPositionOverride = () => {
        setFormData(prev => ({
            ...prev,
            positionOverrides: [...prev.positionOverrides, { positionCode: '', year: new Date().getFullYear() }]
        }))
    }

    const handleRemovePositionOverride = (index: number) => {
        setFormData(prev => ({
            ...prev,
            positionOverrides: prev.positionOverrides.filter((_, i) => i !== index)
        }))
    }

    const handleResetToRookie = () => {
        const currentYear = getCurrentDate().getFullYear()
        const rookieContract = [
            { year: currentYear, yrOption: null, salary: 0.4 },
            { year: currentYear + 1, yrOption: null, salary: 0.4 },
            { year: currentYear + 2, yrOption: null, salary: 0.4 },
            { year: currentYear + 3, yrOption: null, salary: 0 },
            { year: currentYear + 4, yrOption: null, salary: 0 },
            { year: currentYear + 5, yrOption: null, salary: 0 },
        ]

        setFormData(prev => ({
            ...prev,
            contractYears: rookieContract
        }))
    }

    if (isLoading) {
        return <div>Loading...</div>
    }

    console.log('Current formData:', formData)

    return (
        <form ref={formContainerRef} onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                        id="firstName"
                        value={formData.firstName}
                        onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                    />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                        id="lastName"
                        value={formData.lastName}
                        onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                    />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="type">Type</Label>
                    <Select
                        value={formData.type}
                        onValueChange={(value) => {
                            console.log('Type change triggered with value:', value)
                            if (!value) return // Prevent empty updates
                            setFormData(prev => ({ ...prev, type: value }))
                        }}
                    >
                        <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select type">
                                {formData.type && getOptionLabel(PLAYER_TYPES, formData.type)}
                            </SelectValue>
                        </SelectTrigger>
                        <SelectContent container={formContainerRef.current}>
                            {PLAYER_TYPES.map(type => (
                                <SelectItem key={type.value} value={type.value}>
                                    {type.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-2">
                    <Label htmlFor="throws">Throws</Label>
                    <Select
                        value={formData.throws}
                        onValueChange={(value) => {
                            console.log('Throws change triggered with value:', value)
                            if (!value) return // Prevent empty updates
                            setFormData(prev => ({ ...prev, throws: value }))
                        }}
                    >
                        <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select throws">
                                {formData.throws && getOptionLabel(THROWS_OPTIONS, formData.throws)}
                            </SelectValue>
                        </SelectTrigger>
                        <SelectContent container={formContainerRef.current}>
                            {THROWS_OPTIONS.map(option => (
                                <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-2">
                    <Label htmlFor="bats">Bats</Label>
                    <Select
                        value={formData.bats}
                        onValueChange={(value) => {
                            console.log('Bats change triggered with value:', value)
                            if (!value) return // Prevent empty updates
                            setFormData(prev => ({ ...prev, bats: value }))
                        }}
                    >
                        <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select bats">
                                {formData.bats && getOptionLabel(BATS_OPTIONS, formData.bats)}
                            </SelectValue>
                        </SelectTrigger>
                        <SelectContent container={formContainerRef.current}>
                            {BATS_OPTIONS.map(option => (
                                <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-2">
                    <Label htmlFor="lodPlayerId">LOD Player ID</Label>
                    <Input
                        id="lodPlayerId"
                        value={playerId}
                        readOnly
                        className="bg-muted text-muted-foreground cursor-default"
                    />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="mlbPlayerId">MLB Player ID</Label>
                    <Input
                        id="mlbPlayerId"
                        value={formData.mlbPlayerId}
                        onChange={(e) => setFormData(prev => ({ ...prev, mlbPlayerId: e.target.value }))}
                    />
                </div>
                <div className="col-span-2 flex items-center space-x-2">
                    <Checkbox
                        id="exemptFromPlayerCount"
                        checked={formData.exemptFromPlayerCount}
                        onCheckedChange={(checked) =>
                            setFormData(prev => ({ ...prev, exemptFromPlayerCount: checked as boolean }))
                        }
                    />
                    <Label htmlFor="exemptFromPlayerCount">Exempt from Player Count</Label>
                </div>
            </div>

            <div className="space-y-4 rounded-lg border p-4 bg-muted/30">
                <h3 className="text-lg font-medium">Modification Type <span className="text-red-500">*</span></h3>
                <p className="text-sm text-muted-foreground">Select a modification type before editing contract details.</p>
                <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="radio"
                            name="modificationType"
                            value="modification"
                            checked={formData.modificationType === 'modification'}
                            onChange={() => setFormData(prev => ({ ...prev, modificationType: 'modification' }))}
                            className="accent-primary"
                        />
                        <span className="text-sm font-medium">Modifying Existing Contract</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="radio"
                            name="modificationType"
                            value="extension"
                            checked={formData.modificationType === 'extension'}
                            onChange={() => setFormData(prev => ({ ...prev, modificationType: 'extension' }))}
                            className="accent-primary"
                        />
                        <span className="text-sm font-medium">Contract Extension</span>
                    </label>
                </div>
                {formData.modificationType === 'extension' && (
                    <div className="grid grid-cols-2 gap-4 mt-2">
                        <div className="space-y-2">
                            <Label htmlFor="extensionSignedDate">Extension Signed Date *</Label>
                            <Input
                                id="extensionSignedDate"
                                type="date"
                                value={formData.extensionSignedDate}
                                onChange={(e) => setFormData(prev => ({ ...prev, extensionSignedDate: e.target.value }))}
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="cotsUrl">{"Cot's Contracts URL"}</Label>
                            <Input
                                id="cotsUrl"
                                type="url"
                                placeholder="https://..."
                                value={formData.cotsUrl}
                                onChange={(e) => setFormData(prev => ({ ...prev, cotsUrl: e.target.value }))}
                            />
                        </div>
                        <div className="col-span-2 flex items-center space-x-2">
                            <Checkbox
                                id="hasArbYears"
                                checked={formData.hasArbYears}
                                onCheckedChange={(checked) =>
                                    setFormData(prev => ({ ...prev, hasArbYears: checked as boolean }))
                                }
                            />
                            <Label htmlFor="hasArbYears">Player has arbitration-eligible years remaining</Label>
                        </div>
                    </div>
                )}
            </div>

            {formData.modificationType === null ? (
                <div className="text-center py-8 text-muted-foreground border rounded-lg">
                    Select a modification type above to edit contract details.
                </div>
            ) : (<>
            <div className="space-y-4">
                <div className="flex justify-between items-center">
                    <h3 className="text-lg font-medium">
                        {formData.modificationType === 'extension' ? 'New Extension Contract' : 'Contract Years'}
                    </h3>
                    <div className="space-x-2">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={handleResetToRookie}
                        >
                            Reset to Rookie
                        </Button>
                        <Button type="button" variant="outline" onClick={handleAddContractYear}>
                            Add Contract Year
                        </Button>
                    </div>
                </div>
                {formData.contractYears.map((contract, index) => (
                    <div key={index} className="grid grid-cols-4 gap-4 items-end">
                        <div className="space-y-2">
                            <Label>Year</Label>
                            <Input
                                type="number"
                                value={contract.year}
                                onChange={(e) => {
                                    const newContractYears = [...formData.contractYears]
                                    newContractYears[index].year = parseInt(e.target.value)
                                    setFormData(prev => ({ ...prev, contractYears: newContractYears }))
                                }}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Option</Label>
                            <Select
                                value={contract.yrOption || '__none__'}
                                onValueChange={(value) => {
                                    if (!value) return
                                    const newContractYears = [...formData.contractYears]
                                    newContractYears[index].yrOption = value === '__none__' ? null : value
                                    setFormData(prev => ({ ...prev, contractYears: newContractYears }))
                                }}
                            >
                                <SelectTrigger className="w-full">
                                    <SelectValue placeholder="Select option">
                                        {getOptionLabel(OPTION_TYPES, contract.yrOption || '__none__')}
                                    </SelectValue>
                                </SelectTrigger>
                                <SelectContent container={formContainerRef.current}>
                                    {OPTION_TYPES.map(option => (
                                        <SelectItem key={option.value} value={option.value}>
                                            {option.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Salary</Label>
                            <Input
                                type="number"
                                value={contract.salary}
                                onChange={(e) => {
                                    const newContractYears = [...formData.contractYears]
                                    newContractYears[index].salary = parseFloat(e.target.value)
                                    setFormData(prev => ({ ...prev, contractYears: newContractYears }))
                                }}
                            />
                        </div>
                        <Button
                            type="button"
                            variant="destructive"
                            onClick={() => handleRemoveContractYear(index)}
                        >
                            Remove
                        </Button>
                    </div>
                ))}
            </div>

            <div className="space-y-4">
                <div className="flex justify-between items-center">
                    <h3 className="text-lg font-medium">Position Overrides</h3>
                    <Button type="button" variant="outline" onClick={handleAddPositionOverride}>
                        Add Position Override
                    </Button>
                </div>
                {formData.positionOverrides.map((override, index) => (
                    <div key={index} className="grid grid-cols-3 gap-4 items-end">
                        <div className="space-y-2">
                            <Label>Position Code</Label>
                            <Select
                                value={override.positionCode}
                                onValueChange={(value) => {
                                    if (!value) return
                                    const newOverrides = [...formData.positionOverrides]
                                    newOverrides[index].positionCode = value
                                    setFormData(prev => ({ ...prev, positionOverrides: newOverrides }))
                                }}
                            >
                                <SelectTrigger className="w-full">
                                    <SelectValue placeholder="Select position">
                                        {override.positionCode && getOptionLabel(POSITION_OPTIONS, override.positionCode)}
                                    </SelectValue>
                                </SelectTrigger>
                                <SelectContent container={formContainerRef.current}>
                                    {POSITION_OPTIONS.map(option => (
                                        <SelectItem key={option.value} value={option.value}>
                                            {option.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Year</Label>
                            <Input
                                type="number"
                                value={override.year}
                                onChange={(e) => {
                                    const newOverrides = [...formData.positionOverrides]
                                    newOverrides[index].year = parseInt(e.target.value)
                                    setFormData(prev => ({ ...prev, positionOverrides: newOverrides }))
                                }}
                            />
                        </div>
                        <Button
                            type="button"
                            variant="destructive"
                            onClick={() => handleRemovePositionOverride(index)}
                        >
                            Remove
                        </Button>
                    </div>
                ))}
            </div>

            <div className="flex justify-end space-x-2">
                <Button type="button" variant="outline" onClick={() => {
                    console.log('[PlayerEditForm] Cancel button clicked')
                    console.log('[PlayerEditForm] document.body style:', document.body.style.cssText)
                    console.log('[PlayerEditForm] document.body pointerEvents:', document.body.style.pointerEvents)
                    onClose()
                }} disabled={isSubmitting}>
                    Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting || formData.modificationType === null}>
                    {isSubmitting ? "Saving..." : "Save Changes"}
                </Button>
            </div>
            </>)}
        </form>
    )
} 